// src/utils/priceCalculator.js

/**
 * Converts an amount from ILS to the target currency using provided rates.
 * @param {number} amountILS - The amount in ILS to convert.
 * @param {string} targetCurrency - The currency to convert to ('ILS', 'USD', 'EUR').
 * @param {object} rates - An object containing conversion rates, e.g., { usdToIlsRate: 3.7, eurToIlsRate: 4.0 }.
 * @returns {number} The converted amount, rounded to 2 decimal places.
 */
export const convertIlsToTargetCurrency = (amountILS, targetCurrency, rates) => {
  if (amountILS === null || amountILS === undefined || isNaN(amountILS)) {
    return 0;
  }

  if (!rates) {
    console.error('Conversion rates are missing.');
    return amountILS; // Return original amount if rates are not provided
  }

  let convertedAmount = amountILS;

  if (targetCurrency === 'USD' && rates.usdToIlsRate > 0) {
    convertedAmount = amountILS / rates.usdToIlsRate;
  } else if (targetCurrency === 'EUR' && rates.eurToIlsRate > 0) {
    convertedAmount = amountILS / rates.eurToIlsRate;
  }

  // Round to 2 decimal places for currencies
  return Math.round(convertedAmount * 100) / 100;
};

/**
 * Converts an amount from credits to the target currency, using ILS as the base.
 * @param {number} credits - The amount in credits.
 * @param {string} targetCurrency - The currency to convert to ('ILS', 'USD', 'EUR').
 * @param {object} rates - An object containing conversion rates and credit point value, e.g., { usdToIlsRate: 3.7, eurToIlsRate: 4.0, creditPointValueILS: 1.0 }.
 * @returns {number} The converted amount in the target currency.
 */
export const convertCreditsToTargetCurrency = (credits, targetCurrency, rates) => {
  if (!rates || rates.creditPointValueILS === undefined) {
    console.error('Credit point value (creditPointValueILS) is missing in rates object.');
    return 0;
  }
  const amountILS = (credits || 0) * rates.creditPointValueILS;
  // Reuse the existing conversion logic from ILS to the target currency
  return convertIlsToTargetCurrency(amountILS, targetCurrency, rates);
};

/**
 * Formats a number into a currency string based on the specified currency.
 * @param {number} amount - The numeric amount.
 * @param {string} currency - The currency code ('ILS', 'USD', 'EUR').
 * @returns {string} The formatted currency string (e.g., "₪100.00", "$27.03", "€25.00").
 */
export const formatCurrency = (amount, currency = 'ILS') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return ''; // Or a default like 'N/A'
  }

  const localeMap = {
    ILS: 'he-IL',
    USD: 'en-US',
    EUR: 'en-GB', // Use a locale where Euro is common for better formatting
  };
  const locale = localeMap[currency] || 'en-US'; // Fallback to en-US

  const options = {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  let formatted = new Intl.NumberFormat(locale, options).format(amount);
  // The 'he-IL' locale should correctly use '₪'. This is a fallback for environments where it might not.
  if (currency === 'ILS' && formatted.includes('ILS')) {
    formatted = formatted.replace(/ILS/g, '₪').trim();
  }
  return formatted;
};
